import { useEffect, useState, useMemo, useCallback } from 'react';
import { Maybe, OutageZone } from '../../../__generated__/pge-types';
import { useTranslation } from '../../../hooks/useTranslation';
import { PspsStatus } from '../types';
import { Feature } from 'geojson';
import { CONSTANTS } from '../constants';
import { usePspsArcgisData } from './usePspsArcgisData';

export const usePspsHandler = () => {
  const { t } = useTranslation();
  const [pspsData, setPspsData] = useState<Feature[] | null>([]);
  const { pspsGeoJsonData } = usePspsArcgisData({ shouldFetch: true });

  useEffect(() => {
    if (!pspsGeoJsonData) {
      return;
    }
    setPspsData(pspsGeoJsonData.features);
  }, [pspsGeoJsonData]);

  const matchedZones = useMemo(() => {
    if (!pspsData || pspsData.length === 0) {
      return [];
    }

    const uniqueZones = new Map();

    pspsData.forEach(feature => {
      const zoneName = feature.properties?.Name;
      const status = feature.properties?.Status;

      if (zoneName && status) {
        const constantKey = Object.entries(CONSTANTS.PSPS_ZONES).find(
          ([key, value]) => typeof value === 'string' && value === zoneName,
        );

        if (constantKey) {
          const [constName, constValue] = constantKey;

          if (
            !uniqueZones.has(constValue) ||
            uniqueZones.get(constValue).status !== status
          ) {
            uniqueZones.set(constValue, {
              constantName: constName,
              zoneName: constValue,
              status: status,
              originalName: zoneName,
            });
          }
        }
      }
    });

    return Array.from(uniqueZones.values());
  }, [pspsData]);

  const getPspsStatus = useCallback(
    (pspsZone: Maybe<Array<Maybe<OutageZone>>> | undefined | string | null) => {
      let zoneString: string | null = null;

      if (Array.isArray(pspsZone)) {
        zoneString = pspsZone?.[0]?.zone?.toString() || null;
      } else if (typeof pspsZone === 'string') {
        zoneString = pspsZone;
      }

      if (zoneString && matchedZones.length > 0) {
        const matchedZone = matchedZones.find(
          zone =>
            zone.constantName === zoneString ||
            zone.zoneName === zoneString ||
            zone.originalName === zoneString,
        );

        if (matchedZone) {
          return {
            status: matchedZone.status as PspsStatus,
            zone: t(matchedZone.zoneName),
            constantName: matchedZone.constantName,
          };
        }
      }

      return null;
    },
    [matchedZones, t],
  );

  return { getPspsStatus, matchedZones };
};
